import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DemandesRdv from "@/pages/DemandesRdv";
import Integrations from "@/pages/Integrations";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Settings, KeyRound, Users, Eye, EyeOff, Loader2, Check, AlertCircle, RefreshCw,
  Plus, Trash2, Shield, MessageSquareText, Search, Mail, PlugZap, FileText, CalendarClock, HelpCircle,
  Sparkles, Copy, CheckCircle2, Bot,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PlaquesDoublonsCard from "@/components/parametres/PlaquesDoublonsCard";
import PlaquesMultiClientsCard from "@/components/parametres/PlaquesMultiClientsCard";
import ParametresWhatsApp from "@/components/ParametresWhatsApp";

const ROLE_LABELS = {
  admin: "Administrateur",
  comptoir: "Comptoir",
  atelier: "Atelier",
  compta: "Comptabilité",
};

const ROLE_COLORS = {
  admin: "bg-purple-50 text-purple-700 border-purple-200",
  comptoir: "bg-blue-50 text-blue-700 border-blue-200",
  atelier: "bg-orange-50 text-orange-700 border-orange-200",
  compta: "bg-pink-50 text-pink-700 border-pink-200",
};

function PasswordStrength({ pwd }) {
  const checks = [
    { label: "8 caractères minimum", ok: pwd.length >= 8 },
    { label: "Une majuscule", ok: /[A-Z]/.test(pwd) },
    { label: "Une minuscule", ok: /[a-z]/.test(pwd) },
    { label: "Un chiffre", ok: /\d/.test(pwd) },
  ];
  return (
    <ul className="mt-2 space-y-0.5">
      {checks.map((c) => (
        <li key={c.label} className="text-xs flex items-center gap-1.5">
          {c.ok ? <Check className="w-3 h-3 text-green-600" /> : <AlertCircle className="w-3 h-3 text-gray-300" />}
          <span className={c.ok ? "text-green-700" : "text-gray-400"}>{c.label}</span>
        </li>
      ))}
    </ul>
  );
}

function MyPasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (next !== confirm) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Mot de passe modifié. Conservez-le précieusement.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-6 border-gray-200">
      <h2 className="font-heading font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-[#52a675]" /> Changer mon mot de passe
      </h2>
      <p className="text-sm text-gray-500 mb-5">
        Saisissez votre mot de passe actuel puis votre nouveau mot de passe.
      </p>
      <div className="grid md:grid-cols-2 gap-4 max-w-2xl">
        <div className="md:col-span-2">
          <Label>Mot de passe actuel</Label>
          <div className="relative">
            <Input
              type={show ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              data-testid="current-password-input"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label>Nouveau mot de passe</Label>
          <Input
            type={show ? "text" : "password"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            data-testid="new-password-input"
          />
          {next && <PasswordStrength pwd={next} />}
        </div>
        <div>
          <Label>Confirmer le nouveau mot de passe</Label>
          <Input
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            data-testid="confirm-password-input"
          />
          {confirm && next !== confirm && (
            <p className="text-xs text-red-600 mt-1">Les mots de passe ne correspondent pas</p>
          )}
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <Button
          onClick={submit}
          disabled={submitting || !current || !next || next !== confirm}
          className="bg-[#52a675] hover:bg-[#3d8a5c]"
          data-testid="change-password-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Modifier mon mot de passe"}
        </Button>
      </div>
    </Card>
  );
}

function UsersAdminCard() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // iter125 — Création/permissions/suppression user atelier
  const [showAddAtelier, setShowAddAtelier] = useState(false);
  const [permTarget, setPermTarget] = useState(null);  // user atelier en cours d'édition perms
  const [availableTabs, setAvailableTabs] = useState([]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch (e) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    api.get("/auth/available-tabs-atelier").then(({ data }) => setAvailableTabs(data.tabs || [])).catch(() => {});
  }, []);

  // iter125 — Actions sur utilisateur atelier (suppression)
  const deleteAtelierUser = async (u) => {
    if (!window.confirm(`Supprimer ${u.nom} (${u.login}) ?`)) return;
    try {
      await api.delete(`/auth/users/atelier/${u.id}`);
      toast.success("Utilisateur supprimé");
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleActif = async (u) => {
    if (u.id === currentUser.id) {
      toast.error("Vous ne pouvez pas vous désactiver vous-même");
      return;
    }
    try {
      await api.patch(`/auth/users/${u.id}`, { actif: !u.actif });
      toast.success(u.actif ? `${u.nom} désactivé` : `${u.nom} activé`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openResetDialog = (u) => {
    setResetTarget(u);
    setNewPwd(""); setConfirmPwd("");
  };

  const doReset = async () => {
    if (newPwd !== confirmPwd) {
      toast.error("Les deux mots de passe ne correspondent pas");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/auth/users/${resetTarget.id}/reset-password`, { new_password: newPwd });
      toast.success(`Mot de passe de ${resetTarget.nom} réinitialisé. Communiquez-le-lui.`);
      setResetTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-gray-200 overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#52a675]" /> Gestion des utilisateurs
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Réinitialiser un mot de passe, activer/désactiver, gérer les utilisateurs atelier
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowAddAtelier(true)} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="users-add-atelier">
            <Plus className="w-3.5 h-3.5 mr-1" /> Atelier
          </Button>
          <Button variant="outline" size="sm" onClick={fetchUsers} data-testid="users-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {loading && (
          <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        )}
        {!loading && users.map((u) => (
          <div
            key={u.id}
            className="p-4 flex items-center gap-4 flex-wrap"
            data-testid={`user-row-${u.login}`}
          >
            <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-semibold uppercase">
              {u.nom?.[0] || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-gray-900">{u.nom}</div>
                {u.id === currentUser.id && (
                  <Badge variant="outline" className="text-[10px] bg-[#52a675]/10 text-[#3d8a5c] border-[#52a675]/30">
                    Vous
                  </Badge>
                )}
                <Badge variant="outline" className={`text-[10px] ${ROLE_COLORS[u.role]}`}>
                  {ROLE_LABELS[u.role]}
                </Badge>
                {!u.actif && (
                  <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                    Désactivé
                  </Badge>
                )}
                {u.is_service && (
                  <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`badge-service-${u.login}`}>
                    <Bot className="w-3 h-3 mr-1" /> Compte de service
                  </Badge>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Identifiant : <code className="bg-gray-50 px-1 rounded">{u.login}</code></div>
              {u.is_service && (
                <p className="text-[11px] text-gray-500 mt-1 max-w-xl" data-testid={`service-hint-${u.login}`}>
                  Identité de l&apos;assistant IA quand il agit dans le CRM (devis, statuts d&apos;OR,
                  émission de factures). Aucune connexion n&apos;est possible avec ce compte, et il
                  doit rester actif pour que l&apos;assistant fonctionne.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!u.is_service && (
                <>
                  <div className="flex items-center gap-2 mr-2">
                    <span className="text-xs text-gray-500">Actif</span>
                    <Switch
                      checked={u.actif}
                      onCheckedChange={() => toggleActif(u)}
                      disabled={u.id === currentUser.id}
                      data-testid={`toggle-actif-${u.login}`}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openResetDialog(u)}
                    data-testid={`reset-pwd-${u.login}`}
                  >
                    <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                    Réinitialiser
                  </Button>
                </>
              )}
              {u.role === "atelier" && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPermTarget(u)}
                    data-testid={`perms-${u.login}`}
                  >
                    <Shield className="w-3.5 h-3.5 mr-1.5" /> Onglets
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteAtelierUser(u)}
                    disabled={u.id === currentUser.id}
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    data-testid={`del-${u.login}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
            <DialogDescription>
              Vous allez définir un nouveau mot de passe pour <strong>{resetTarget?.nom}</strong> ({resetTarget?.login}).
              <br />
              <span className="text-orange-700">Communiquez-le-lui personnellement après confirmation.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nouveau mot de passe</Label>
              <Input
                type="text"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Ex: Atelier@2026!"
                data-testid="reset-new-password"
              />
              {newPwd && <PasswordStrength pwd={newPwd} />}
            </div>
            <div>
              <Label>Confirmer</Label>
              <Input
                type="text"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                data-testid="reset-confirm-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Annuler</Button>
            <Button
              onClick={doReset}
              disabled={submitting || !newPwd || newPwd !== confirmPwd}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="reset-pwd-confirm"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Réinitialiser"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter125 — Création d'un user atelier */}
      <AddAtelierUserDialog
        open={showAddAtelier}
        onClose={() => setShowAddAtelier(false)}
        availableTabs={availableTabs}
        onCreated={() => { setShowAddAtelier(false); fetchUsers(); }}
      />

      {/* iter125 — Édition des permissions onglets d'un user atelier */}
      <AtelierPermissionsDialog
        target={permTarget}
        onClose={() => setPermTarget(null)}
        availableTabs={availableTabs}
        onSaved={() => { setPermTarget(null); fetchUsers(); }}
      />
    </Card>
  );
}

// ============================================================
// iter125 — Dialogs atelier user management
// ============================================================
function AddAtelierUserDialog({ open, onClose, availableTabs, onCreated }) {
  const [login, setLogin] = useState("");
  const [nom, setNom] = useState("");
  const [pwd, setPwd] = useState("");
  const [perms, setPerms] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (open) { setLogin(""); setNom(""); setPwd(""); setPerms([]); }
  }, [open]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const togglePerm = (t) => setPerms((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);

  const submit = async () => {
    if (!login || !nom || !pwd) { toast.error("Login, nom et mot de passe requis"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/users/atelier", { login, nom, password: pwd, permissions: perms });
      toast.success(`Utilisateur « ${login} » créé`);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-atelier-dialog">
        <DialogHeader>
          <DialogTitle>Nouvel utilisateur atelier</DialogTitle>
          <DialogDescription>Rôle <b>atelier</b> uniquement. Sélectionne les onglets accessibles.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-700">Login *</label>
            <input value={login} onChange={(e) => setLogin(e.target.value.toLowerCase())} className="w-full px-3 py-2 border rounded text-sm" placeholder="ex: meca_jean" data-testid="add-login" style={{ fontSize: 16 }} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Nom affiché *</label>
            <input value={nom} onChange={(e) => setNom(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" data-testid="add-nom" style={{ fontSize: 16 }} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Mot de passe *</label>
            <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" placeholder="min 8 char, 1 maj, 1 chiffre, 1 spécial" data-testid="add-pwd" style={{ fontSize: 16 }} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Onglets autorisés</label>
            <div className="grid grid-cols-2 gap-2">
              {availableTabs.map((t) => (
                <label key={t} className="flex items-center gap-2 p-2 border border-gray-200 rounded cursor-pointer hover:bg-gray-50" data-testid={`tab-${t}`}>
                  <input type="checkbox" checked={perms.includes(t)} onChange={() => togglePerm(t)} />
                  <span className="text-sm capitalize">{t.replace("_", " ")}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="add-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Créer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AtelierPermissionsDialog({ target, onClose, availableTabs, onSaved }) {
  const [perms, setPerms] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (target) setPerms(target.permissions_atelier || []);
  }, [target?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (!target) return null;
  const togglePerm = (t) => setPerms((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);
  const save = async () => {
    setSubmitting(true);
    try {
      await api.patch(`/auth/users/atelier/${target.id}/permissions`, { permissions: perms });
      toast.success("Permissions mises à jour");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="perms-dialog">
        <DialogHeader>
          <DialogTitle>Onglets — {target.nom}</DialogTitle>
          <DialogDescription>Sélectionne les onglets accessibles à <b>{target.login}</b>.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 my-3">
          {availableTabs.map((t) => (
            <label key={t} className="flex items-center gap-2 p-2 border border-gray-200 rounded cursor-pointer hover:bg-gray-50" data-testid={`perm-tab-${t}`}>
              <input type="checkbox" checked={perms.includes(t)} onChange={() => togglePerm(t)} />
              <span className="text-sm capitalize">{t.replace("_", " ")}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="perms-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Parametres() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const canVoirWidget = isAdmin || user?.role === "comptoir";   // iter309 — Widget RDV

  // Lecture du tab depuis l'URL ?tab=...
  // iter337 — la flotte de remplacement a déménagé dans « Voiture de prêt »
  const params = new URLSearchParams(window.location.search);
  const tabDemande = params.get("tab");
  const defaultTab = (!tabDemande || tabDemande === "flotte") ? "profil" : tabDemande;
  useEffect(() => {
    if (tabDemande === "flotte") navigate("/remplacement?tab=flotte", { replace: true });
  }, [tabDemande, navigate]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="parametres-page">
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-1">
        <Settings className="w-7 h-7 text-[#52a675]" /> Paramètres
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Gérez votre compte, les utilisateurs et la configuration du garage
      </p>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="w-full overflow-x-auto justify-start no-scrollbar flex-nowrap">
          <TabsTrigger value="profil" data-testid="tab-mon-profil">Mon profil</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="utilisateurs" data-testid="tab-utilisateurs">Utilisateurs</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="configuration" data-testid="tab-configuration">Configuration</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="sms-twilio" data-testid="tab-sms-twilio">SMS Twilio</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="promotions" data-testid="tab-promotions">Promotions</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="mail" data-testid="tab-mail">Mail</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="compta-tools" data-testid="tab-compta-tools">Compta</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="ibans" data-testid="tab-ibans">IBAN fournisseurs</TabsTrigger>
          )}
          {canVoirWidget && (
            <TabsTrigger value="widget-rdv" data-testid="tab-widget-rdv">Widget RDV</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="integrations" data-testid="tab-integrations">Intégrations</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profil" className="mt-4 space-y-4">
          <Card className="p-5 border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#52a675] text-white flex items-center justify-center text-xl font-bold">
                {user?.nom?.[0] || "?"}
              </div>
              <div>
                <div className="font-heading font-bold text-lg text-gray-900">{user?.nom}</div>
                <div className="text-sm text-gray-500">
                  Identifiant : <code className="bg-gray-50 px-1 rounded">{user?.login}</code>
                  {" · "}
                  <Badge variant="outline" className={`text-[10px] ${ROLE_COLORS[user?.role]}`}>
                    {ROLE_LABELS[user?.role]}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>
          <MyPasswordCard />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="utilisateurs" className="mt-4">
            <UsersAdminCard />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="configuration" className="mt-4 space-y-4">
            <PlaquesDoublonsCard />
            <PlaquesMultiClientsCard />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="sms-twilio" className="mt-4 space-y-4">
            {/* iter338 — Les réglages d'envoi de SMS vivent avec l'historique Twilio */}
            <AppSettingsCard />
            <SmsTwilioTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="whatsapp" className="mt-4">
            <ParametresWhatsApp />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="promotions" className="mt-4">
            <PromotionsTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="mail" className="mt-4">
            <MailImapTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="compta-tools" className="mt-4">
            <ComptaToolsTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="ibans" className="mt-4">
            <FournisseursIbansTab />
          </TabsContent>
        )}
        {/* iter309 — déplacés du menu latéral vers des sous-catégories des Paramètres */}
        {canVoirWidget && (
          <TabsContent value="widget-rdv" className="mt-4 -mx-4 sm:-mx-6 lg:-mx-8" data-testid="parametres-widget-rdv">
            <DemandesRdv />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="integrations" className="mt-4 -mx-4 sm:-mx-6 lg:-mx-8" data-testid="parametres-integrations">
            <Integrations />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}



// ============================================================
// iter228 — Registre des IBAN fournisseurs récurrents
// ============================================================
function FournisseursIbansTab() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({ nom: "", iban: "", notes: "" });
  const [saving, setSaving] = React.useState(false);
  const [editId, setEditId] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get("/fournisseurs-ibans")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Chargement des IBAN impossible"))
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.nom.trim() || !form.iban.trim()) return toast.error("Nom et IBAN obligatoires");
    setSaving(true);
    try {
      if (editId) await api.put(`/fournisseurs-ibans/${editId}`, form);
      else await api.post("/fournisseurs-ibans", form);
      toast.success(editId ? "IBAN mis à jour" : "IBAN ajouté");
      setForm({ nom: "", iban: "", notes: "" });
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet IBAN de référence ?")) return;
    try {
      await api.delete(`/fournisseurs-ibans/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const fmtIban = (v) => (v || "").replace(/(.{4})/g, "$1 ").trim();

  return (
    <Card className="p-5 border-gray-200" data-testid="ibans-card">
      <h2 className="font-heading font-bold text-gray-900">IBAN fournisseurs récurrents</h2>
      <p className="text-xs text-gray-500 mt-1">
        L&apos;IBAN de référence de chaque fournisseur (Würth, Derendinger…). Il complète une facture
        sans IBAN et déclenche une alerte à l&apos;export si l&apos;IBAN lu sur la facture est différent.
        Les QR-IBAN détectés dans un QR-bill sont enregistrés automatiquement.
      </p>

      <div className="grid md:grid-cols-[1fr_1.2fr_1fr_auto] gap-2 items-end mt-4 pb-4 border-b border-gray-100">
        <div>
          <Label className="text-xs">Fournisseur</Label>
          <Input value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
            placeholder="Würth AG" data-testid="iban-form-nom" />
        </div>
        <div>
          <Label className="text-xs">IBAN (ou QR-IBAN)</Label>
          <Input value={form.iban} onChange={(e) => setForm((f) => ({ ...f, iban: e.target.value }))}
            placeholder="CH93 3000 5233 8932 9001 G" className="font-mono" data-testid="iban-form-iban" />
        </div>
        <div>
          <Label className="text-xs">Note (optionnel)</Label>
          <Input value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Compte pièces" data-testid="iban-form-notes" />
        </div>
        <div className="flex gap-2">
          {editId && (
            <Button variant="outline" onClick={() => { setEditId(null); setForm({ nom: "", iban: "", notes: "" }); }}>
              Annuler
            </Button>
          )}
          <Button onClick={submit} disabled={saving} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="iban-form-submit">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : editId ? "Enregistrer" : <Plus className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400" data-testid="ibans-empty">
          Aucun IBAN enregistré pour le moment.
        </div>
      ) : (
        <div className="divide-y divide-gray-100 mt-2">
          {items.map((it) => (
            <div key={it.id} className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`iban-row-${it.id}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-semibold text-gray-900">{it.nom}</div>
                <code className="text-xs text-gray-600 font-mono">{fmtIban(it.iban)}</code>
                {it.notes && <div className="text-[11px] text-gray-400">{it.notes}</div>}
              </div>
              {it.is_qr_iban && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">QR-IBAN</Badge>
              )}
              <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-600">
                {it.source === "auto_qr" ? "appris du QR-bill" : "saisi manuellement"}
              </Badge>
              <Button size="sm" variant="ghost" onClick={() => { setEditId(it.id); setForm({ nom: it.nom, iban: it.iban, notes: it.notes || "" }); }}
                data-testid={`iban-edit-${it.id}`}>
                Modifier
              </Button>
              <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                onClick={() => remove(it.id)} data-testid={`iban-delete-${it.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AppSettingsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setItems(data || []);
    } catch (e) {
      toast.error("Impossible de charger les paramètres");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const toggle = async (key, value) => {
    setSavingKey(key);
    try {
      await api.put(`/settings/${key}`, { value });
      setItems((prev) => prev.map((it) => (it.key === key ? { ...it, value, is_default: false } : it)));
      toast.success("Paramètre enregistré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSavingKey(null); }
  };

  if (loading) {
    return (
      <Card className="p-6 border-gray-200 text-center text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </Card>
    );
  }

  return (
    <Card className="p-5 border-gray-200" data-testid="app-settings-card">
      <div className="flex items-center gap-2 mb-4">
        <Settings className="w-5 h-5 text-[#52a675]" />
        <h2 className="font-heading font-bold text-lg text-gray-900">Configuration applicative</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Activez ou désactivez les fonctions automatiques. Les changements prennent effet immédiatement.
      </p>
      <div className="divide-y divide-gray-100">
        {items.map((it) => (
          <div key={it.key} className="py-4 flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-gray-900">{it.label}</div>
              {it.description && (
                <div className="text-xs text-gray-500 mt-1">{it.description}</div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {savingKey === it.key && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
              {it.type === "color" ? (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={it.value || "#52a675"}
                    onChange={(e) => toggle(it.key, e.target.value)}
                    disabled={savingKey === it.key}
                    className="w-10 h-8 rounded border border-gray-200 cursor-pointer bg-white"
                    data-testid={`setting-color-${it.key}`}
                  />
                  <span className="text-xs font-mono text-gray-500">{it.value}</span>
                </div>
              ) : (
                <Switch
                  checked={!!it.value}
                  onCheckedChange={(v) => toggle(it.key, v)}
                  disabled={savingKey === it.key}
                  data-testid={`setting-toggle-${it.key}`}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}



/**
 * iter132 — Onglet SMS Twilio (admin only).
 * Affiche l'historique des SMS envoyés (mock + envoyés + bloqués + erreurs).
 * Filtres : statut + recherche texte (numéro ou message).
 */
const STATUS_COLORS = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  mocked: "bg-gray-50 text-gray-600 border-gray-200",
  mocked_preview_blocked: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_LABELS = {
  sent: "Envoyé",
  mocked: "Mocké (Twilio désactivé)",
  mocked_preview_blocked: "Bloqué (preview)",
  error: "Erreur",
};

function fmtSmsDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CH", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/**
 * iter286 — Onglet Promotions (admin).
 * Promos créées avec une campagne SMS ou déclarées à l'assistant pour une
 * campagne externe (e-mail, Google, Meta). Léa les annonce, les OR les portent.
 */
function PromotionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toutes, setToutes] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/promos", { params: { toutes } });
      setItems(data.items || []);
    } catch {
      toast.error("Chargement des promotions impossible");
    } finally {
      setLoading(false);
    }
  }, [toutes]);

  useEffect(() => { load(); }, [load]);

  const basculer = async (p) => {
    try {
      await api.patch(`/promos/${p.id}`, { actif: !p.actif });
      toast.success(p.actif ? "Promotion désactivée" : "Promotion réactivée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modification impossible");
    }
  };

  const prolonger = async (p) => {
    const nouvelle = window.prompt("Nouvelle date de fin (AAAA-MM-JJ)", p.date_fin);
    if (!nouvelle) return;
    try {
      await api.patch(`/promos/${p.id}`, { date_fin: nouvelle.trim() });
      toast.success("Promotion prolongée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modification impossible");
    }
  };

  return (
    <Card className="p-4" data-testid="promotions-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Promotions</h3>
          <p className="text-xs text-gray-500">
            Léa les annonce aux clients concernés et elles se posent automatiquement sur les OR correspondants.
            Demandez-en une à l&apos;assistant IA (« j&apos;ai lancé une campagne Meta : check-up offert avec les pneus »).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setToutes(!toutes)} data-testid="promos-filtre">
            {toutes ? "Voir les actives" : "Voir toutes"}
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="promos-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {!items.length ? (
        <p className="text-xs text-gray-500" data-testid="promos-vide">Aucune promotion enregistrée.</p>
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.id} className="rounded-lg border border-gray-200 p-3" data-testid="promo-item">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                    {p.libelle}
                    <Badge variant="outline" className={p.actif
                      ? "bg-[#52a675]/15 text-[#2d5f3f] border-[#52a675]/40"
                      : "bg-gray-50 text-gray-500 border-gray-200"}>
                      {p.actif ? "active" : "inactive"}
                    </Badge>
                    {p.canal && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{p.canal}</Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5">
                    {p.offert_resume} · déclencheurs : {(p.declencheurs || []).join(", ") || "tous"}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5" data-testid="promo-periode">
                    du {p.date_debut} au {p.date_fin}
                    {p.campagne?.statut === "programmee" && p.campagne?.envoi_prevu
                      ? ` · campagne programmée le ${fmtSmsDate(p.campagne.envoi_prevu)}`
                      : ""}
                  </div>
                  {/* iter287 — rendement : SMS → leads Léa → OR → CHF facturés */}
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="promo-rendement">
                    {[
                      p.rendement?.sms_envoyes
                        ? { t: `${p.rendement.sms_envoyes} SMS (${p.rendement.cout_sms_chf.toFixed(2)} CHF)` }
                        : null,
                      p.nb_beneficiaires ? { t: `${p.nb_beneficiaires} ciblés` } : null,
                      { t: `${p.rendement?.leads_lea || 0} leads Léa` },
                      { t: `${p.rendement?.nb_or || 0} OR` },
                      { t: `${(p.rendement?.ca_facture_chf || 0).toFixed(2)} CHF facturés`, fort: true },
                      p.rendement?.offert_chf
                        ? { t: `dont ${p.rendement.offert_chf.toFixed(2)} CHF offerts` }
                        : null,
                    ].filter(Boolean).map(({ t, fort }) => (
                      <span
                        key={t}
                        className={`text-[11px] rounded-full border px-2 py-0.5 ${
                          fort
                            ? "bg-[#f0f9f4] border-[#52a675]/40 text-[#2d5f3f] font-semibold"
                            : "bg-gray-50 border-gray-200 text-gray-600"
                        }`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => prolonger(p)} data-testid={`promo-prolonger-${p.id}`}>
                    Prolonger
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => basculer(p)} data-testid={`promo-toggle-${p.id}`}>
                    {p.actif ? "Désactiver" : "Réactiver"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SmsTwilioTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  // iter285 — campagnes lancées depuis l'assistant + réponses entrantes
  const [campagnes, setCampagnes] = useState([]);
  const [reponses, setReponses] = useState({ items: [], desinscrits: [], nb_desinscrits: 0 });
  // iter288 — pieds de page SMS modifiables (transactionnel / campagne)
  const [footers, setFooters] = useState({ transactionnel: "", campagne: "" });
  const [footersDefauts, setFootersDefauts] = useState({ transactionnel: "", campagne: "" });
  const [savingFooters, setSavingFooters] = useState(false);
  // iter292 — réglages du rappel automatique de rendez-vous
  const [rappels, setRappels] = useState({
    valeurs: { actif: true, heure: 17, jours_avant: 1 }, dernier_envoi: null,
  });
  const [savingRappels, setSavingRappels] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (q.trim()) params.set("q", q.trim());
      if (statusFilter) params.set("status", statusFilter);
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get(`/admin/sms?${params.toString()}`),
        api.get("/admin/sms/stats"),
      ]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setStats(st);
      const [{ data: camp }, { data: rep }] = await Promise.all([
        api.get("/admin/sms/campagnes"),
        api.get("/admin/sms/reponses"),
      ]);
      setCampagnes(camp.items || []);
      setReponses(rep);
      const { data: f } = await api.get("/admin/sms/footers");
      setFooters(f.valeurs);
      setFootersDefauts(f.defauts);
      const { data: rp } = await api.get("/admin/rdv-rappels/config");
      setRappels({ valeurs: rp.valeurs, dernier_envoi: rp.dernier_envoi });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger l'historique SMS");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = (e) => {
    e?.preventDefault?.();
    load();
  };

  // iter288 — une campagne programmée reste annulable jusqu'à son départ
  const annulerCampagne = async (c) => {
    if (!window.confirm(`Annuler cette campagne ?\n\n« ${c.texte} »`)) return;
    try {
      await api.post(`/admin/sms/campagnes/${c.id}/annuler`);
      toast.success("Campagne annulée — rien ne sera envoyé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Annulation impossible");
    }
  };

  const enregistrerRappels = async () => {
    setSavingRappels(true);
    try {
      const { data } = await api.put("/admin/rdv-rappels/config", rappels.valeurs);
      setRappels((r) => ({ ...r, valeurs: data.valeurs }));
      toast.success(
        data.valeurs.actif
          ? `Rappels envoyés à ${String(data.valeurs.heure).padStart(2, "0")}h00, ${data.valeurs.jours_avant === 1 ? "la veille" : "2 jours avant"}`
          : "Rappels automatiques désactivés",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSavingRappels(false);
    }
  };

  const enregistrerFooters = async () => {
    setSavingFooters(true);
    try {
      const { data } = await api.put("/admin/sms/footers", footers);
      setFooters(data.valeurs);
      toast.success("Pieds de page enregistrés — appliqués aux prochains envois");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSavingFooters(false);
    }
  };

  return (
    <Card className="p-5 border-gray-200" data-testid="sms-twilio-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#52a675]/10 text-[#52a675] flex items-center justify-center">
            <MessageSquareText className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-gray-900">SMS Twilio</h2>
            <p className="text-xs text-gray-500">
              SMS sortants, campagnes et réponses entrantes
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="sms-twilio-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      {/* iter285 — Campagnes SMS lancées depuis l'assistant IA */}
      <div className="mb-5 rounded-xl border border-gray-200 overflow-hidden" data-testid="sms-campagnes">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">
            Campagnes SMS ({campagnes.length})
          </span>
          <span className="text-[11px] text-gray-500" data-testid="sms-desinscrits-count">
            {reponses.nb_desinscrits} désinscrit{reponses.nb_desinscrits > 1 ? "s" : ""} (STOP)
          </span>
        </div>
        {!campagnes.length ? (
          <p className="px-3 py-3 text-xs text-gray-500">
            Aucune campagne. Demandez-en une à l&apos;assistant IA (« envoie une campagne SMS pour les pneus hiver »).
          </p>
        ) : (
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {campagnes.map((c) => (
              <div key={c.id} className="px-3 py-2 text-xs" data-testid="sms-campagne-item">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{fmtSmsDate(c.created_at)}</span>
                  <span className="text-gray-500">par {c.user_nom || "—"}</span>
                  <Badge variant="outline" className={
                    c.statut === "terminee" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : c.statut === "en_cours" ? "bg-blue-50 text-blue-700 border-blue-200"
                        : c.statut === "programmee" ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-gray-50 text-gray-600 border-gray-200"
                  }>
                    {c.statut === "programmee" ? "programmée" : c.statut}
                  </Badge>
                  {c.origine === "gardiennage" && (
                    <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                      relance pneus
                    </Badge>
                  )}
                  <span className="text-gray-700">
                    {c.statut === "programmee"
                      ? `${c.total || 0} destinataires · départ le ${fmtSmsDate(c.envoi_prevu)}`
                      : `${c.envoyes || 0}/${c.total || 0} envoyés${c.simules ? ` · ${c.simules} simulés` : ""}${c.echecs ? ` · ${c.echecs} échecs` : ""}`}
                  </span>
                  {["programmee", "preparee", "test_envoye"].includes(c.statut) && (
                    <Button
                      size="sm" variant="ghost"
                      className="h-6 text-[11px] text-red-600 hover:text-red-700"
                      onClick={() => annulerCampagne(c)}
                      data-testid={`sms-campagne-annuler-${c.id}`}
                    >
                      Annuler
                    </Button>
                  )}
                </div>
                <div className="text-gray-600 mt-0.5">« {c.texte} »</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* iter292 — Rappel automatique la veille des rendez-vous */}
      <div className="mb-5 rounded-xl border border-gray-200 overflow-hidden" data-testid="rdv-rappels-config">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-gray-700">Rappel automatique des rendez-vous</span>
          {rappels.dernier_envoi && (
            <span className="text-[11px] text-gray-500">
              Dernier envoi : {rappels.dernier_envoi.envoyes + (rappels.dernier_envoi.simules || 0)} SMS
              pour le {rappels.dernier_envoi.jour}
            </span>
          )}
        </div>
        <div className="p-3 space-y-3">
          <label className="flex items-center gap-2 text-xs text-gray-800">
            <input
              type="checkbox"
              checked={!!rappels.valeurs.actif}
              onChange={(e) => setRappels((r) => ({ ...r, valeurs: { ...r.valeurs, actif: e.target.checked } }))}
              className="accent-[#52a675] w-4 h-4"
              data-testid="rdv-rappels-actif"
            />
            Envoyer les rappels automatiquement
          </label>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Heure d'envoi</Label>
              <Select
                value={String(rappels.valeurs.heure)}
                onValueChange={(v) => setRappels((r) => ({ ...r, valeurs: { ...r.valeurs, heure: Number(v) } }))}
              >
                <SelectTrigger className="h-9 w-28 mt-1 text-xs" data-testid="rdv-rappels-heure">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 16 }, (_, i) => i + 6).map((h) => (
                    <SelectItem key={h} value={String(h)} data-testid={`rdv-rappels-heure-${h}`}>
                      {String(h).padStart(2, "0")}h00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Combien de temps avant</Label>
              <Select
                value={String(rappels.valeurs.jours_avant)}
                onValueChange={(v) => setRappels((r) => ({ ...r, valeurs: { ...r.valeurs, jours_avant: Number(v) } }))}
              >
                <SelectTrigger className="h-9 w-40 mt-1 text-xs" data-testid="rdv-rappels-jours">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1" data-testid="rdv-rappels-jours-1">La veille</SelectItem>
                  <SelectItem value="2" data-testid="rdv-rappels-jours-2">2 jours avant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              size="sm"
              onClick={enregistrerRappels}
              disabled={savingRappels}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="rdv-rappels-save"
            >
              {savingRappels ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Enregistrer
            </Button>
          </div>
          <p className="text-[11px] text-gray-500">
            Les clients attendus (rendez-vous planifiés et confirmés) reçoivent un SMS à l'heure choisie.
            Un rendez-vous n'est jamais rappelé deux fois, et les annulés sont ignorés.
          </p>
        </div>
      </div>

      {/* iter288 — Pieds de page ajoutés automatiquement à chaque SMS */}
      <div className="mb-5 rounded-xl border border-gray-200 overflow-hidden" data-testid="sms-footers">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <span className="text-xs font-semibold text-gray-700">Pieds de page ajoutés à chaque SMS</span>
        </div>
        <div className="p-3 space-y-3">
          {[
            {
              cle: "transactionnel",
              titre: "SMS de suivi",
              aide: "Confirmations de rendez-vous, véhicule prêt, rappels de facture.",
            },
            {
              cle: "campagne",
              titre: "Campagnes et offres",
              aide: "Doit contenir le mot STOP : c'est la réponse qui désinscrit le client automatiquement.",
            },
          ].map(({ cle, titre, aide }) => (
            <div key={cle}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className="text-xs font-medium text-gray-900" htmlFor={`footer-${cle}`}>{titre}</label>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-400">{(footers[cle] || "").length}/160</span>
                  <button
                    type="button"
                    className="text-[11px] text-gray-500 hover:text-gray-800 underline"
                    onClick={() => setFooters((f) => ({ ...f, [cle]: footersDefauts[cle] }))}
                    data-testid={`sms-footer-reset-${cle}`}
                  >
                    Texte par défaut
                  </button>
                </div>
              </div>
              <Textarea
                id={`footer-${cle}`}
                value={footers[cle] || ""}
                onChange={(e) => setFooters((f) => ({ ...f, [cle]: e.target.value }))}
                rows={2}
                maxLength={160}
                className="mt-1 text-xs resize-none"
                data-testid={`sms-footer-${cle}`}
              />
              <p className="text-[11px] text-gray-500 mt-1">{aide}</p>
            </div>
          ))}
          <Button
            size="sm"
            onClick={enregistrerFooters}
            disabled={savingFooters}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="sms-footers-save"
          >
            {savingFooters ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Enregistrer les pieds de page
          </Button>
        </div>
      </div>

      {/* iter285 — Réponses SMS entrantes (webhook Twilio) */}
      <div className="mb-5 rounded-xl border border-gray-200 overflow-hidden" data-testid="sms-reponses">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <span className="text-xs font-semibold text-gray-700">Réponses reçues ({reponses.items.length})</span>
        </div>
        {!reponses.items.length ? (
          <p className="px-3 py-3 text-xs text-gray-500">
            Aucune réponse. Configurez dans Twilio, sur votre numéro d&apos;envoi, le webhook
            « A message comes in » → POST <code>/api/public/twilio/sms-inbound</code> pour recevoir
            les réponses et traiter automatiquement les STOP.
          </p>
        ) : (
          <div className="divide-y divide-gray-100 max-h-56 overflow-y-auto">
            {reponses.items.map((r) => (
              <div key={r.id} className="px-3 py-2 text-xs flex items-start gap-2" data-testid="sms-reponse-item">
                <span className="font-medium text-gray-900 shrink-0">{r.numero}</span>
                <span className="text-gray-600 flex-1">{r.texte}</span>
                {r.optout && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">STOP</Badge>
                )}
                <span className="text-gray-400 shrink-0">{fmtSmsDate(r.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>



      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <div className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total</div>
          <div className="text-lg font-bold text-gray-900" data-testid="sms-stat-total">{stats.total}</div>
        </div>
        {Object.entries(stats.by_status || {}).map(([k, v]) => (
          <div key={k} className={`rounded-lg px-3 py-2 border ${STATUS_COLORS[k] || "bg-gray-50 text-gray-700 border-gray-200"}`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{STATUS_LABELS[k] || k}</div>
            <div className="text-lg font-bold" data-testid={`sms-stat-${k}`}>{v}</div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <form onSubmit={applyFilters} className="flex flex-wrap items-end gap-2 mb-4">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">Rechercher</Label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Numéro ou contenu du message…"
              className="pl-7"
              data-testid="sms-twilio-search"
            />
          </div>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-[10px] uppercase text-gray-500 mb-1 block">Statut</Label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            data-testid="sms-twilio-status-filter"
          >
            <option value="">Tous</option>
            <option value="sent">Envoyés</option>
            <option value="mocked">Mockés</option>
            <option value="mocked_preview_blocked">Bloqués (preview)</option>
            <option value="error">Erreurs</option>
          </select>
        </div>
        <Button type="submit" disabled={loading} data-testid="sms-twilio-apply">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Filtrer"}
        </Button>
      </form>

      {/* Liste */}
      <div className="text-xs text-gray-500 mb-2" data-testid="sms-twilio-count">
        {total} SMS · {items.length} affichés
      </div>

      {loading && items.length === 0 ? (
        <div className="py-8 text-center text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400" data-testid="sms-twilio-empty">
          Aucun SMS enregistré. Les SMS sortants apparaissent ici dès qu&apos;ils sont envoyés.
        </div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
          {items.map((s) => (
            <div key={s.id} className="p-3 hover:bg-gray-50/50" data-testid={`sms-row-${s.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="font-mono text-sm font-semibold text-gray-900">{s.to || "—"}</code>
                  <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[s.status] || "bg-gray-50"}`}>
                    {STATUS_LABELS[s.status] || s.status}
                  </Badge>
                  {s.twilio_sid && (
                    <code className="text-[10px] text-gray-400 font-mono" title="SID Twilio">
                      {s.twilio_sid.slice(0, 12)}…
                    </code>
                  )}
                </div>
                <div className="text-[11px] text-gray-400">{fmtSmsDate(s.created_at)}</div>
              </div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap break-words mt-1">
                {s.message}
              </div>
              {s.error && (
                <div className="mt-1.5 text-xs text-red-600 bg-red-50 px-2 py-1 rounded inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {s.error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


// ============================================================
// Task #4 — Onglet Mail (Paramètres) : config IMAP Infomaniak + auto-tri
// ============================================================

function fmtMailDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("fr-CH", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return v; }
}

function SupplierDomainsEditor({ value, onChange }) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const v = draft.trim().toLowerCase().replace(/^@/, "");
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div data-testid="ff-supplier-domains">
      <div className="flex gap-2 mt-1">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="ex. technomag.ch ou compta@derendinger.ch"
          data-testid="ff-domain-input"
        />
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="ff-domain-add-btn">
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
      {value.length === 0 ? (
        <p className="text-xs text-gray-400 mt-2" data-testid="ff-domain-empty">
          Aucun domaine de confiance. Les factures seront détectées via les mots-clés et le QR-bill uniquement.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((d) => (
            <span
              key={d}
              className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded-full pl-2.5 pr-1 py-1"
              data-testid={`ff-domain-chip-${d}`}
            >
              {d}
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== d))}
                className="p-0.5 rounded-full hover:bg-red-100 hover:text-red-600"
                data-testid={`ff-domain-remove-${d}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function MailImapTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/mail/config");
      setCfg(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger la configuration mail");
    } finally { setLoading(false); }
  };


  useEffect(() => { load(); }, []);

  const patch = (field, value) => setCfg((c) => ({ ...c, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      delete payload.password_set;
      delete payload.last_poll_at;
      delete payload.last_poll_status;
      delete payload.last_poll_error;
      delete payload.last_poll_stats;
      delete payload.updated_at;
      delete payload.updated_by;
      delete payload.id;
      if (pwd.trim()) payload.imap_password = pwd.trim();
      const { data } = await api.put("/admin/mail/config", payload);
      setCfg(data);
      setPwd("");
      toast.success("Configuration mail enregistrée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const body = pwd.trim() ? { imap_password: pwd.trim() } : {};
      const { data } = await api.post("/admin/mail/test-connection", body);
      setTestResult(data);
      if (data.ok) toast.success(`Connexion OK — ${data.unseen_count ?? 0} email(s) non lu(s)`);
      else toast.error(data.error || "Échec de connexion");
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || "Erreur" });
      toast.error("Échec du test de connexion");
    } finally { setTesting(false); }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/admin/mail/poll-now");
      if (data.status === "ok") {
        const s = data.stats || {};
        toast.success(`Relevé terminé : ${s.factures_fournisseurs || 0} facture(s) créée(s), ${s.ignores ?? 0} message(s) laissé(s) intact(s)`);
      } else if (data.status === "no_password") {
        toast.error("Identifiants IMAP incomplets — enregistre la configuration d'abord");
      } else if (data.status === "error") {
        toast.error(data.error || "Erreur de sondage");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de sondage");
    } finally { setPolling(false); }
  };

  if (loading || !cfg) {
    return (
      <Card className="p-6 border-gray-200 text-center text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* iter198 — Signature globale du garage (injectée dans TOUS les mails sortants) */}
      <MailSignatureCard />

      <Card className="p-5 border-gray-200" data-testid="mail-config-card">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-[#52a675]/10 text-[#52a675] flex items-center justify-center">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-gray-900">Relevé des factures fournisseurs (IMAP)</h2>
              <p className="text-xs text-gray-500">
                Relevé automatique trois fois par jour (7h, 12h et 18h) et sur clic.
                Seules les factures fournisseurs sont reprises (au statut à valider) ; les
                autres messages ne sont ni enregistrés, ni marqués comme lus, ni modifiés.
              </p>
            </div>
          </div>
        </div>

        {cfg.last_poll_at && (
          <div className="mt-3 text-xs px-3 py-2 rounded-lg border bg-gray-50 border-gray-200 text-gray-700 flex items-center gap-2 flex-wrap" data-testid="mail-last-poll">
            <span className="font-semibold">Dernier relevé :</span> {fmtMailDate(cfg.last_poll_at)}
            <Badge
              variant="outline"
              className={
                cfg.last_poll_status === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : cfg.last_poll_status === "disabled" ? "bg-gray-50 text-gray-600 border-gray-200"
                : "bg-red-50 text-red-700 border-red-200"
              }
            >
              {cfg.last_poll_status || "—"}
            </Badge>
            {cfg.last_poll_stats && (
              <span className="text-gray-500">
                {cfg.last_poll_stats.factures_fournisseurs || 0} facture(s) créée(s) ·{" "}
                {cfg.last_poll_stats.ignores ?? cfg.last_poll_stats.logged ?? 0} message(s) laissé(s) intact(s)
                {cfg.last_poll_stats.errors ? ` · ${cfg.last_poll_stats.errors} erreur(s)` : ""}
              </span>
            )}
            {cfg.last_poll_error && <span className="text-red-600">— {cfg.last_poll_error}</span>}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>Serveur IMAP</Label>
            <Input value={cfg.imap_host || ""} onChange={(e) => patch("imap_host", e.target.value)} placeholder="mail.infomaniak.com" data-testid="mail-host" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Port</Label>
              <Input type="number" value={cfg.imap_port || 993} onChange={(e) => patch("imap_port", e.target.value)} data-testid="mail-port" />
            </div>
            <div className="flex items-end gap-2 pb-1.5">
              <Switch checked={!!cfg.use_ssl} onCheckedChange={(v) => patch("use_ssl", v)} data-testid="mail-ssl-switch" />
              <span className="text-sm text-gray-700">SSL/TLS</span>
            </div>
          </div>
          <div>
            <Label>Nom d'utilisateur (email)</Label>
            <Input value={cfg.imap_username || ""} onChange={(e) => patch("imap_username", e.target.value)} placeholder="meyrin@car-main.ch" data-testid="mail-username" />
          </div>
          <div>
            <Label>Mot de passe {cfg.password_set && <span className="text-xs text-emerald-600 font-normal">(déjà configuré)</span>}</Label>
            <div className="relative">
              <Input
                type={showPwd ? "text" : "password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder={cfg.password_set ? "Laisser vide pour ne pas changer" : "Mot de passe applicatif Infomaniak"}
                className="pr-10"
                data-testid="mail-password"
              />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>Dossier surveillé</Label>
            <Input value={cfg.folder || "INBOX"} onChange={(e) => patch("folder", e.target.value)} data-testid="mail-folder" />
          </div>
        </div>

        {/* iter224 — Pré-filtre factures fournisseurs */}
        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="flex items-start gap-2 mb-2">
            <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Pré-filtre « Factures fournisseurs »</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Une facture n&apos;est créée que si : mot-clé facture (objet ou nom du fichier),
                expéditeur fournisseur connu, ou QR-bill suisse détecté. Les devis, bons de livraison,
                rappels, newsletters et signatures/logos sont ignorés — ces messages restent non lus
                dans votre boîte et ne laissent aucune trace dans le CRM.
              </p>
            </div>
          </div>
          <Label className="text-xs">Domaines / adresses fournisseurs de confiance</Label>
          <SupplierDomainsEditor
            value={cfg.ff_supplier_domains || []}
            onChange={(v) => patch("ff_supplier_domains", v)}
          />
        </div>

        {testResult && (
          <div className={`mt-3 text-xs px-3 py-2 rounded-lg border flex items-center gap-2 ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="mail-test-result">
            {testResult.ok ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
            {testResult.ok
              ? `Connexion réussie — ${testResult.message_count ?? "?"} email(s) dans le dossier, ${testResult.unseen_count ?? 0} non lu(s).`
              : (testResult.error || "Échec de connexion")}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={testConnection} disabled={testing} data-testid="mail-test-btn">
            {testing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5 mr-1.5" />}
            Tester la connexion
          </Button>
          <Button variant="outline" size="sm" onClick={pollNow} disabled={polling} data-testid="mail-poll-now-btn">
            {polling ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Relever maintenant
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#52a675] hover:bg-[#3d8a5c]" size="sm" data-testid="mail-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : "Enregistrer"}
          </Button>
        </div>
      </Card>

      {/* iter257 — L'historique des emails traités a été retiré : la boîte mail
          complète est désormais accessible depuis le menu « Boîte mail ». */}
    </div>
  );
}

// ============================================================
// iter198 — Signature globale (injectée dans TOUS les mails sortants)
// Éditeur riche minimal contenteditable qui accepte le copier-coller
// d'images (logos) — encodées automatiquement en base64 data-URI par le
// navigateur (paste natif). Aucune upload serveur nécessaire.
//
// iter211 — Fix "logos apparaissent en attachement fantôme sur Mac Mail" :
// intercepte le paste pour convertir toute image collée en data-URI base64
// (certaines apps macOS insèrent des `<img src="webkit-fake-url://...">`
// ou `src="filename.jpeg"` non résolvables). Ajoute aussi un bouton
// d'upload direct pour éviter tout aléa. Nettoie les <img> orphelines
// à la sauvegarde.
// ============================================================
function MailSignatureCard() {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const editorRef = React.useRef(null);
  const logoInputRef = React.useRef(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mail/signature");
        setHtml(data.html || "");
        if (editorRef.current) editorRef.current.innerHTML = data.html || "";
      } catch (e) {
        if (e?.response?.status !== 403) toast.error("Erreur chargement signature");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    if (editorRef.current) setHtml(editorRef.current.innerHTML);
  };

  // Encode un File/Blob en data-URI base64
  const fileToDataUri = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  // Insère un img data-URI dans l'éditeur à la position courante
  const insertImage = (dataUri) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    const img = `<img src="${dataUri}" alt="logo" style="max-height:64px;vertical-align:middle;margin:2px 4px 2px 0"/>`;
    document.execCommand("insertHTML", false, img);
    setHtml(editorRef.current.innerHTML);
  };

  // iter211 — Intercepte le paste : si un fichier image est dans le clipboard,
  // le convertit en data-URI. Sinon laisse le comportement natif.
  const onPaste = async (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        e.preventDefault();
        const file = it.getAsFile();
        if (!file) return;
        try {
          const uri = await fileToDataUri(file);
          insertImage(uri);
        } catch {
          toast.error("Impossible d'encoder l'image collée");
        }
        return;
      }
    }
    // Sinon : le HTML collé peut contenir des <img src=".../.jpeg"> non résolvables.
    // On les repère après le paste et on prévient l'utilisateur.
    setTimeout(() => {
      if (!editorRef.current) return;
      const imgs = editorRef.current.querySelectorAll("img");
      let brokenCount = 0;
      imgs.forEach((im) => {
        const src = im.getAttribute("src") || "";
        if (!/^(data:image\/|https?:\/\/)/i.test(src)) brokenCount += 1;
      });
      if (brokenCount > 0) {
        toast.warning(
          `${brokenCount} image(s) collée(s) non embarquée(s) — utilise plutôt le bouton « Ajouter un logo » ou copie l'image directement depuis un aperçu.`,
        );
      }
      setHtml(editorRef.current.innerHTML);
    }, 50);
  };

  // Bouton "Ajouter un logo" : file picker → data-URI → insert
  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Merci de sélectionner une image (PNG, JPG, SVG…)");
      return;
    }
    if (file.size > 500 * 1024) {
      toast.warning(`Logo volumineux (${Math.round(file.size / 1024)} Ko) — la signature va alourdir chaque email.`);
    }
    try {
      const uri = await fileToDataUri(file);
      insertImage(uri);
    } catch {
      toast.error("Impossible de lire le fichier");
    }
    e.target.value = "";
  };

  // iter211 — Nettoie les <img> orphelines à la sauvegarde
  const sanitizeAndSave = async () => {
    if (!editorRef.current) return;
    setSaving(true);
    try {
      // Retire les <img> dont le src n'est ni data-URI ni http(s)
      const clone = editorRef.current.cloneNode(true);
      const badImgs = clone.querySelectorAll("img");
      let removed = 0;
      badImgs.forEach((im) => {
        const src = im.getAttribute("src") || "";
        if (!/^(data:image\/|https?:\/\/)/i.test(src)) {
          im.remove();
          removed += 1;
        }
      });
      if (removed > 0) {
        toast.warning(`${removed} image(s) non embarquable(s) retirée(s) avant enregistrement.`);
      }
      const currentHtml = clone.innerHTML;
      await api.put("/mail/signature", { html: currentHtml });
      setHtml(currentHtml);
      // Reflète la version nettoyée dans l'éditeur
      editorRef.current.innerHTML = currentHtml;
      toast.success("Signature enregistrée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white" data-testid="mail-signature-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-gray-900">Signature du garage</h2>
            <p className="text-xs text-gray-500">
              Injectée automatiquement en fin de <b>tous</b> les emails sortants (boîte mail, factures, devis, rappels).
              Utilise « Ajouter un logo » pour insérer une image de manière fiable.
            </p>
          </div>
        </div>
      </div>

      {/* Toolbar minimale */}
      <div className="flex items-center gap-1 mb-2 p-1 bg-white border border-gray-200 rounded flex-wrap" data-testid="signature-toolbar">
        <button type="button" onClick={() => exec("bold")} className="px-2 py-1 text-sm font-bold hover:bg-gray-100 rounded" title="Gras">B</button>
        <button type="button" onClick={() => exec("italic")} className="px-2 py-1 text-sm italic hover:bg-gray-100 rounded" title="Italique">I</button>
        <button type="button" onClick={() => exec("underline")} className="px-2 py-1 text-sm underline hover:bg-gray-100 rounded" title="Souligné">U</button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button type="button" onClick={() => {
          const url = window.prompt("URL du lien (https://...) :");
          if (url) exec("createLink", url);
        }} className="px-2 py-1 text-sm hover:bg-gray-100 rounded" title="Lien">🔗</button>
        <button type="button" onClick={() => exec("removeFormat")} className="px-2 py-1 text-xs hover:bg-gray-100 rounded" title="Effacer format">Aucun format</button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => logoInputRef.current?.click()}
          className="px-2 py-1 text-xs bg-emerald-100 text-emerald-800 hover:bg-emerald-200 rounded font-medium"
          title="Insérer un logo depuis un fichier (recommandé pour macOS)"
          data-testid="signature-add-logo-btn"
        >
          + Ajouter un logo
        </button>
        <input
          ref={logoInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
          className="hidden"
          onChange={onPickLogo}
          data-testid="signature-logo-input"
        />
        <div className="flex-1" />
        <span className="text-[11px] text-gray-400 mr-2">Ctrl+V ou glisse une image ici</span>
      </div>

      {/* Zone contenteditable */}
      {loading ? (
        <div className="p-4 text-sm text-gray-400">Chargement…</div>
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={(e) => setHtml(e.currentTarget.innerHTML)}
          onPaste={onPaste}
          className="min-h-[140px] p-3 border border-gray-300 rounded bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="signature-editor"
          suppressContentEditableWarning
        />
      )}

      {/* iter217 — Aperçu de la signature actuellement enregistrée en base */}
      {!loading && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-2">
            <span>Aperçu — signature enregistrée en base</span>
            {html && html !== (editorRef.current?.innerHTML || "") && (
              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[10px]">
                Modifs non enregistrées
              </Badge>
            )}
          </div>
          {html ? (
            <div
              className="border-2 border-dashed border-emerald-300 rounded p-3 bg-white text-sm"
              data-testid="signature-preview"
              // Le HTML est celui de l'admin lui-même — pas de risque XSS externe
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded p-4 text-xs text-gray-400 italic text-center bg-gray-50" data-testid="signature-preview-empty">
              Aucune signature enregistrée — remplis l&apos;éditeur ci-dessus et clique « Enregistrer ».
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <Button onClick={sanitizeAndSave} disabled={saving || loading} data-testid="signature-save-btn" className="bg-emerald-600 hover:bg-emerald-700">
          {saving ? "Enregistrement…" : "Enregistrer la signature"}
        </Button>
      </div>
    </Card>
  );
}


// ============================================================
// iter203 — Outils compta admin : diagnostic & réparation des
// factures dont un encaissement CAMT a été validé mais n'a
// pas été inscrit sur la facture (statut resté « en_suspens »).
// ============================================================
function ComptaToolsTab() {
  const [scanResult, setScanResult] = React.useState(null); // { scanned, orphans_found, details }
  const [scanning, setScanning] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [applied, setApplied] = React.useState(null);

  // iter204 — état pour la 2ème carte : reconstruction factures orphelines OR
  const [scanFacOR, setScanFacOR] = React.useState(null);
  const [scanningFacOR, setScanningFacOR] = React.useState(false);
  const [applyingFacOR, setApplyingFacOR] = React.useState(false);
  const [appliedFacOR, setAppliedFacOR] = React.useState(null);

  const runDryRun = async () => {
    setScanning(true);
    setScanResult(null);
    setApplied(null);
    try {
      const { data } = await api.post("/journal/factures/backfill-encaissements-camt", { dry_run: true });
      setScanResult(data);
      if (data.orphans_found === 0) {
        toast.success("Aucune facture à réparer — tout est cohérent ✅");
      } else {
        toast.info(`${data.orphans_found} facture(s) à réparer détectée(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur pendant le diagnostic");
    } finally {
      setScanning(false);
    }
  };

  const runApply = async () => {
    if (!scanResult || scanResult.orphans_found === 0) return;
    if (!window.confirm(
      `Confirmer la réparation de ${scanResult.orphans_found} facture(s) ?\n\n` +
      "Un encaissement sera ajouté à chaque facture concernée et son statut sera recalculé (partiel/payée).\n" +
      "L'opération est idempotente et peut être rejouée sans risque."
    )) return;
    setApplying(true);
    try {
      const { data } = await api.post("/journal/factures/backfill-encaissements-camt", { dry_run: false });
      setApplied(data);
      toast.success(`${data.repaired} facture(s) réparée(s) ✅`);
      // Relance un scan pour confirmer que tout est propre
      await runDryRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur pendant la réparation");
    } finally {
      setApplying(false);
    }
  };

  // iter204 — Reconstruction factures orphelines OR
  const runDryRunFacOR = async () => {
    setScanningFacOR(true);
    setScanFacOR(null);
    setAppliedFacOR(null);
    try {
      const { data } = await api.post("/journal/factures/backfill-factures-orphelines-or", { dry_run: true });
      setScanFacOR(data);
      if (data.orphans_found === 0) {
        toast.success("Aucun OR sans facture — tout est cohérent ✅");
      } else {
        toast.info(`${data.orphans_found} OR sans facture correspondante détecté(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur pendant le diagnostic");
    } finally {
      setScanningFacOR(false);
    }
  };

  const runApplyFacOR = async () => {
    if (!scanFacOR || scanFacOR.orphans_found === 0) return;
    if (!window.confirm(
      `Confirmer la reconstruction de ${scanFacOR.orphans_found} facture(s) depuis les OR ?\n\n` +
      "Un document facture sera créé pour chaque OR concerné en reprenant ses lignes, totaux et client. " +
      "Statut initial : « en_suspens ». Aucune écriture comptable n'est générée (les écritures historiques restent inchangées).\n" +
      "L'opération est idempotente."
    )) return;
    setApplyingFacOR(true);
    try {
      const { data } = await api.post("/journal/factures/backfill-factures-orphelines-or", { dry_run: false });
      setAppliedFacOR(data);
      toast.success(`${data.repaired} facture(s) reconstruite(s) ✅`);
      await runDryRunFacOR();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur pendant la reconstruction");
    } finally {
      setApplyingFacOR(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 border-gray-200">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-lg text-gray-900 mb-1">
              🔧 Réparation des rapprochements CAMT
            </h3>
            <p className="text-sm text-gray-600">
              Détecte et répare les factures dont un paiement bancaire a été validé
              dans le journal comptable, mais dont l&apos;encaissement n&apos;a jamais été
              inscrit sur la fiche facture (statut resté « en suspens » à tort).
              Idempotent, rejouable sans risque.
            </p>
          </div>
          <div className="shrink-0">
            <Button
              onClick={runDryRun}
              disabled={scanning || applying}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="camt-backfill-diagnose-btn"
            >
              {scanning ? "Analyse…" : "🔍 Diagnostiquer"}
            </Button>
          </div>
        </div>

        {scanResult && (
          <div className="mt-4 space-y-3">
            <div className={`p-3 rounded border text-sm ${
              scanResult.orphans_found === 0
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-amber-50 border-amber-200 text-amber-900"
            }`} data-testid="camt-backfill-scan-summary">
              <div className="font-semibold">
                Analyse terminée : {scanResult.scanned_provisoires} écritures CAMT contrôlées
              </div>
              <div>
                {scanResult.orphans_found === 0
                  ? "✅ Aucune facture à réparer — toutes les écritures sont bien répercutées sur les fiches factures."
                  : `⚠️ ${scanResult.orphans_found} facture(s) présente(nt) un décalage à réparer.`}
              </div>
            </div>

            {scanResult.orphans_found > 0 && (
              <>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-xs" data-testid="camt-backfill-orphans-table">
                    <thead className="bg-gray-50 text-gray-700 border-b">
                      <tr>
                        <th className="text-left px-3 py-2">N° facture</th>
                        <th className="text-left px-3 py-2">Statut actuel</th>
                        <th className="text-right px-3 py-2">Montant CAMT</th>
                        <th className="text-left px-3 py-2">Date valeur</th>
                        <th className="text-left px-3 py-2">Libellé bancaire</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scanResult.details.map((d, i) => (
                        <tr key={i} className="border-b last:border-b-0 hover:bg-gray-50" data-testid={`camt-backfill-orphan-row-${i}`}>
                          <td className="px-3 py-2 font-mono font-semibold">{d.facture_numero || "—"}</td>
                          <td className="px-3 py-2">
                            <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-[10px] uppercase">
                              {d.facture_statut_avant || "n/a"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">CHF {Number(d.montant_alloue || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 font-mono">{d.date_valeur || "—"}</td>
                          <td className="px-3 py-2 truncate max-w-xs" title={d.libelle_bancaire}>{d.libelle_bancaire || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {scanResult.details_truncated && (
                    <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t">
                      Aperçu limité aux 200 premières — l&apos;opération de réparation traite bien 100% des cas trouvés.
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={runApply}
                    disabled={applying || scanning}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="camt-backfill-apply-btn"
                  >
                    {applying ? "Réparation en cours…" : `✅ Réparer les ${scanResult.orphans_found} facture(s)`}
                  </Button>
                </div>
              </>
            )}

            {applied && applied.repaired > 0 && (
              <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-900" data-testid="camt-backfill-apply-summary">
                ✅ <strong>{applied.repaired} facture(s) réparée(s)</strong> — les statuts ont été recalculés
                automatiquement (partiel/payée). Une nouvelle analyse a été lancée ci-dessus pour confirmer.
              </div>
            )}
          </div>
        )}
      </Card>

      {/* iter204 — Reconstruction factures orphelines depuis un OR */}
      <Card className="p-5 border-gray-200">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-lg text-gray-900 mb-1">
              📄 Reconstruction des factures manquantes (OR sans facture)
            </h3>
            <p className="text-sm text-gray-600">
              Détecte les OR qui référencent une facture (bouton « Aperçu facture QR-bill » visible)
              mais dont le document facture est absent de l&apos;onglet Factures. Typiquement des OR
              importés depuis Guido/Bexio. Reconstruit la facture manquante à partir des données
              de l&apos;OR (lignes, totaux, client). Statut initial : « en suspens ». Aucune
              écriture comptable n&apos;est créée. Idempotent.
            </p>
          </div>
          <div className="shrink-0">
            <Button
              onClick={runDryRunFacOR}
              disabled={scanningFacOR || applyingFacOR}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="or-facture-backfill-diagnose-btn"
            >
              {scanningFacOR ? "Analyse…" : "🔍 Diagnostiquer"}
            </Button>
          </div>
        </div>

        {scanFacOR && (
          <div className="mt-4 space-y-3">
            <div className={`p-3 rounded border text-sm ${
              scanFacOR.orphans_found === 0
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-amber-50 border-amber-200 text-amber-900"
            }`} data-testid="or-facture-backfill-scan-summary">
              <div className="font-semibold">
                Analyse terminée : {scanFacOR.scanned_or} OR contrôlés
              </div>
              <div>
                {scanFacOR.orphans_found === 0
                  ? "✅ Aucun OR sans facture correspondante — toutes les factures existent bien."
                  : `⚠️ ${scanFacOR.orphans_found} OR référence(nt) une facture qui n'existe pas dans la liste Factures.`}
              </div>
            </div>

            {scanFacOR.orphans_found > 0 && (
              <>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-xs" data-testid="or-facture-backfill-orphans-table">
                    <thead className="bg-gray-50 text-gray-700 border-b">
                      <tr>
                        <th className="text-left px-3 py-2">N° OR</th>
                        <th className="text-left px-3 py-2">N° facture attendu</th>
                        <th className="text-left px-3 py-2">Client</th>
                        <th className="text-left px-3 py-2">Plaque</th>
                        <th className="text-left px-3 py-2">Date OR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scanFacOR.details.map((d, i) => (
                        <tr key={i} className="border-b last:border-b-0 hover:bg-gray-50" data-testid={`or-facture-orphan-row-${i}`}>
                          <td className="px-3 py-2 font-mono font-semibold">{d.or_numero || "—"}</td>
                          <td className="px-3 py-2 font-mono">{d.facture_numero || (d.facture_id ? `id ${String(d.facture_id).slice(0,8)}…` : "—")}</td>
                          <td className="px-3 py-2 truncate max-w-xs" title={d.client_nom}>{d.client_nom || "—"}</td>
                          <td className="px-3 py-2 font-mono">{d.vehicule_plaque || "—"}</td>
                          <td className="px-3 py-2 font-mono">{(d.or_created_at || "").slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {scanFacOR.details_truncated && (
                    <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t">
                      Aperçu limité aux 200 premières — l&apos;opération de reconstruction traite bien 100% des cas trouvés.
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={runApplyFacOR}
                    disabled={applyingFacOR || scanningFacOR}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="or-facture-backfill-apply-btn"
                  >
                    {applyingFacOR ? "Reconstruction en cours…" : `✅ Reconstruire les ${scanFacOR.orphans_found} facture(s)`}
                  </Button>
                </div>
              </>
            )}

            {appliedFacOR && appliedFacOR.repaired > 0 && (
              <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-900" data-testid="or-facture-backfill-apply-summary">
                ✅ <strong>{appliedFacOR.repaired} facture(s) reconstruite(s)</strong> — elles apparaissent
                désormais dans l&apos;onglet Factures avec le statut « en_suspens ». Vérifie/ajuste manuellement
                leur statut si elles étaient déjà encaissées.
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
